import { useState } from 'react';
import PropTypes from 'prop-types';
import AppLink from '../../links/AppLink';
import clsx from 'clsx';
import { Image, SvgIcon } from 'edcom-ui';
import { tabInfoPropType, isSeasonalNavButtonActive } from '../util';
import styles from './SubNav.module.less';
import { trackGA } from 'services/track';

const SubNavButton = ({ tabInfo, openDropdown, closeDropdown, selected }) => {
  const { name, urlName, hasDropdown, seasonalInfo } = tabInfo;
  const [selectorOpen, setSelectorOpen] = useState(false);

  // Exit early if this is an inactive seasonal link
  if (seasonalInfo && !isSeasonalNavButtonActive(seasonalInfo)) {
    return null;
  }

  // Seasonal links can include slashes (e.g. "/halloween/") while regular buttons don't (e.g. "worksheet")
  const buttonUrl = urlName.includes('/') ? urlName : `/${urlName}/`;

  /** Instantly open or close the dropdown associated with this button */
  const instantToggleDropdown = () =>
    selected ? closeDropdown(false) : openDropdown(tabInfo, false);

  // The "More" tab is not clickable and shouldn't have an <a> tag
  const isLink = name !== 'More';

  const handleSubNavButtonClick = () => {
    closeDropdown();
    setSelectorOpen(false);

    if (!window?.location?.hostname) {
      return null;
    }

    const gtmObject = {
      event: 'click_button',
      feature: 'Navigation',
      section: 'Main Menu',
      button: 'Main Menu Link',
      click_text: name,
      click_url: `https://${window.location.hostname}${buttonUrl}`,
    };
    trackGA('Main Menu', 'click_button', '', '', gtmObject);
  };

  return (
    <div
      className={clsx(
        styles.subNavButton,
        (selectorOpen || selected) && styles.selected,
        !isLink && styles.notClickable
      )}
      onMouseEnter={() => {
        setSelectorOpen(true);
        openDropdown(tabInfo);
      }}
      onMouseLeave={() => setSelectorOpen(false)}
    >
      <div className={styles.buttonsContainer}>
        <ConditionalAppLink
          isLink={isLink}
          onClick={handleSubNavButtonClick}
          href={buttonUrl}
          className={styles.buttonLink}
          aria-expanded={selected}
        >
          {seasonalInfo && seasonalInfo.icon && (
            <Image alt="" src={seasonalInfo.icon} />
          )}
          <span className={styles.buttonText}>{name}</span>
        </ConditionalAppLink>
        {hasDropdown && (
          <button
            className={styles.openDropdownButton}
            type="button"
            aria-expanded={selected}
            aria-label={`${name} menu`}
            onClick={instantToggleDropdown}
          >
            <SvgIcon
              name="angleBottom"
              className={clsx(styles.chevron, selected && styles.inverted)}
              aria-hidden
            />
          </button>
        )}
      </div>
      <div
        className={clsx(styles.selector, !hasDropdown && styles.longerSelector)}
      />
    </div>
  );
};

SubNavButton.propTypes = {
  tabInfo: tabInfoPropType.isRequired,
  openDropdown: PropTypes.func.isRequired,
  closeDropdown: PropTypes.func.isRequired,
  selected: PropTypes.bool,
};

SubNavButton.defaultProps = {
  selected: false,
};

export default SubNavButton;

/**
 * The "More" tab doesn't have a link, so I'm adding this new component here to reduce the amount of duplicated code
 * @returns {JSX.Element}
 */
const ConditionalAppLink = ({ children, isLink, ...otherProps }) => {
  if (isLink) {
    return <AppLink {...otherProps}>{children}</AppLink>;
  }
  return <div className={otherProps.className}>{children}</div>;
};

ConditionalAppLink.propTypes = {
  children: PropTypes.node.isRequired,
  isLink: PropTypes.bool.isRequired,
};
